// Level progression — the ECOSYSTEM game's own, since the puzzle/biome
// split's step 4 (docs/puzzle-biome-split.md, option B): puzzle-solving
// is endless practice, every difficulty available from the start,
// completely independent of level. This file no longer has a puzzle-side
// field at all — `stableGoal` is the one number per level: the length of
// the balanced-cycle streak that completes it, and the only thing that
// does. It used to ALSO be the count of puzzle rewards the ecosystem had
// to receive that level, which broke progression outright: the goal line
// promised the player N cycles, so a perfectly balanced biome held for 40
// of them just never completed (see docs/decisionlog.md). Purely data —
// the engine/UI just read this array. To add a level: insert an entry
// anywhere and everything (goal display, completion modal) picks it up
// automatically.
//
// requiredCategories / minCategoryTypes / minSpeciesTypes: a plants-only biome is a
// zero-risk, permanent win for the stability half of the game — nothing
// preys on or grazes a plant, so it never has anything to fail against
// (confirmed empirically: even the catalog's two slowest-growing plants
// hold perfect stability straight through Level 7's 30-cycle goal).
// These two fields turn each level's own `desc` — which already narrates
// an intended composition ("a herbivore" at Seedling, "predators" at
// Grove, "decomposers" at Canopy) but previously had zero mechanical
// effect — into a real gate on starting/stepping the simulation. Sprout
// is deliberately exempt (`[]`/`1`): its own desc describes a single
// plant thriving, which is the intended first tutorial moment, not a
// loophole to close. See systems/progression.js's meetsDiversityGoal and
// docs/decisionlog.md.
//
// minCategoryTypes counts CATEGORIES (plants / herbivores / predators /
// decomposers); minSpeciesTypes counts distinct SPECIES — Grass and
// Wildflower are one category but two species types, while two separately
// earned Rabbits ("Snow Rabbit", "Whispering Rabbit") are two tiles but
// only one type. The category count alone let a player carry a
// two-species biome (one plant, one herbivore) all the way through
// Seedling and Sapling and arrive at Grove still running the minimum
// build every level's `desc` had already told them to outgrow — Seedling
// literally promises "Two plants and an herbivore". minSpeciesTypes makes
// that promise mechanical, and it is what actually makes each level a
// wider web than the last: category count saturates at 4 by Canopy, so
// without it there is nothing left to ask for.
//
// Neither floor may exceed what a level can actually AWARD:
// games/ecosystem/systems/speciesFactory.js's categoriesForLevel() only introduces
// predators at Grove and decomposers at Canopy, so Sapling — which can
// only ever grant plants + herbivores — sits at 2 CATEGORIES. It was
// briefly 3, which left players unable to earn a 3rd category and
// permanently unable to run the sim at that level. See docs/decisionlog.md.
// minSpeciesTypes has no such ceiling (both of those categories hold six
// or seven distinct species) but must stay under `maxSpecies`, or the
// level asks for more kinds than the biome has room to hold.
export const LEVELS = [
  {
    name: "Sprout",
    maxSpecies: 3,
    stableGoal: 10,
    desc: "A single plant species thriving. The beginning of life.",
    requiredCategories: [],
    minCategoryTypes: 1,
    minSpeciesTypes: 1,
  },
  {
    name: "Seedling",
    maxSpecies: 5,
    stableGoal: 10,
    desc: "Two plants and an herbivore, living in balance.",
    requiredCategories: ["herbivores"],
    minCategoryTypes: 2,
    // The desc has always said three; now it means it.
    minSpeciesTypes: 3,
  },
  {
    name: "Sapling",
    maxSpecies: 7,
    stableGoal: 10,
    desc: "A balanced plant-and-herbivore food chain. Predators arrive next.",
    requiredCategories: ["herbivores"],
    minCategoryTypes: 2,
    // Holds Seedling's three; the step up here is the second herbivore its
    // award pool starts favouring, not a wider count.
    minSpeciesTypes: 3,
  },
  {
    name: "Grove",
    maxSpecies: 10,
    stableGoal: 10,
    desc: "Add predators to the mix. Keep everyone fed.",
    requiredCategories: ["herbivores", "predators"],
    minCategoryTypes: 3,
    // A predator on top of a one-plant, one-herbivore chain is a spike, not
    // a web: the fourth type is what gives the prey tier something to fall
    // back on.
    minSpeciesTypes: 4,
  },
  {
    name: "Canopy",
    maxSpecies: 12,
    stableGoal: 15,
    desc: "Full food web with decomposers. True ecosystem.",
    requiredCategories: ["herbivores", "predators", "decomposers"],
    minCategoryTypes: 4,
    // All four categories already force four distinct species, so this is
    // a floor rather than a new demand — the last three levels are the
    // hardest in the game (measured: 17% / 13% / 8% of random builds win)
    // and did not need another one. Raise it here if they ever want to ask
    // for breadth on top of the full web.
    minSpeciesTypes: 4,
  },
  {
    name: "Meadow",
    maxSpecies: 15,
    stableGoal: 15,
    desc: "Survive a drought while keeping everything stable.",
    requiredCategories: ["herbivores", "predators", "decomposers"],
    minCategoryTypes: 4,
    minSpeciesTypes: 4,
  },
  {
    name: "Sanctuary",
    maxSpecies: 99,
    stableGoal: 20,
    desc: "Mastery. Unlimited species. Maintain harmony.",
    requiredCategories: ["herbivores", "predators", "decomposers"],
    minCategoryTypes: 4,
    minSpeciesTypes: 4,
  },
];
